import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BookOpen, ChevronDown, ChevronUp } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useLanguage } from "@/contexts/LanguageContext";

interface BibleVerseProps {
  reference: string;
  compact?: boolean;
}

interface BibleData {
  text: string;
  reference: string;
  translation_name: string;
  actualTranslation?: string; // Track which translation was actually used
}

// Map languages to Bolls.life translation IDs
const BIBLE_VERSIONS: Record<string, string> = {
  en: "KJV", // King James Version
  ru: "SYNOD", // Russian Synodal
  es: "RVA", // Reina-Valera Actualizada (primary)
};

// Spanish translation fallbacks (try in order if primary fails)
const SPANISH_FALLBACKS = ["RVA", "LBLA", "NVI", "DHH"];

// Book name to ID mapping for Bolls.life API
const BOOK_IDS: Record<string, number> = {
  genesis: 1, exodus: 2, leviticus: 3, numbers: 4, deuteronomy: 5,
  joshua: 6, judges: 7, ruth: 8, "1samuel": 9, "2samuel": 10,
  "1kings": 11, "2kings": 12, "1chronicles": 13, "2chronicles": 14,
  ezra: 15, nehemiah: 16, esther: 17, job: 18, psalms: 19,
  proverbs: 20, ecclesiastes: 21, songofsolomon: 22, isaiah: 23,
  jeremiah: 24, lamentations: 25, ezekiel: 26, daniel: 27,
  hosea: 28, joel: 29, amos: 30, obadiah: 31, jonah: 32,
  micah: 33, nahum: 34, habakkuk: 35, zephaniah: 36,
  haggai: 37, zechariah: 38, malachi: 39,
  matthew: 40, mark: 41, luke: 42, john: 43, acts: 44,
  romans: 45, "1corinthians": 46, "2corinthians": 47,
  galatians: 48, ephesians: 49, philippians: 50,
  colossians: 51, "1thessalonians": 52, "2thessalonians": 53,
  "1timothy": 54, "2timothy": 55, titus: 56, philemon: 57,
  hebrews: 58, james: 59, "1peter": 60, "2peter": 61,
  "1john": 62, "2john": 63, "3john": 64, jude: 65, revelation: 66
};

// Book name translations
const BOOK_NAMES: Record<string, Record<string, string>> = {
  en: {
    genesis: "genesis", exodus: "exodus", leviticus: "leviticus", numbers: "numbers",
    deuteronomy: "deuteronomy", joshua: "joshua", judges: "judges", ruth: "ruth",
    "1 samuel": "1samuel", "2 samuel": "2samuel", "1 kings": "1kings", "2 kings": "2kings",
    "1 chronicles": "1chronicles", "2 chronicles": "2chronicles", ezra: "ezra", nehemiah: "nehemiah",
    esther: "esther", job: "job", psalms: "psalms", psalm: "psalms", proverbs: "proverbs",
    ecclesiastes: "ecclesiastes", "song of solomon": "songofsolomon", isaiah: "isaiah",
    jeremiah: "jeremiah", lamentations: "lamentations", "lamentations jeremiah": "lamentations", "lamentations of jeremiah": "lamentations", ezekiel: "ezekiel", daniel: "daniel",
    hosea: "hosea", joel: "joel", amos: "amos", obadiah: "obadiah", jonah: "jonah",
    micah: "micah", nahum: "nahum", habakkuk: "habakkuk", zephaniah: "zephaniah",
    haggai: "haggai", zechariah: "zechariah", malachi: "malachi",
    matthew: "matthew", mark: "mark", luke: "luke", john: "john", acts: "acts",
    romans: "romans", "1 corinthians": "1corinthians", "2 corinthians": "2corinthians",
    galatians: "galatians", ephesians: "ephesians", "eph": "ephesians", "eph.": "ephesians", philippians: "philippians",
    colossians: "colossians", "1 thessalonians": "1thessalonians", "2 thessalonians": "2thessalonians",
    "1 timothy": "1timothy", "2 timothy": "2timothy", titus: "titus", philemon: "philemon",
    hebrews: "hebrews", "heb": "hebrews", "heb.": "hebrews", james: "james", "1 peter": "1peter", "2 peter": "2peter",
    "1 john": "1john", "2 john": "2john", "3 john": "3john", jude: "jude", revelation: "revelation"
  },
  ru: {
    бытие: "genesis", бытия: "genesis", исход: "exodus", исхода: "exodus", левит: "leviticus", левита: "leviticus", числа: "numbers", чисел: "numbers",
    второзаконие: "deuteronomy", второзакония: "deuteronomy", иисус: "joshua", навин: "joshua", навина: "joshua", судьи: "judges", судей: "judges", руфь: "ruth", руфи: "ruth",
    "1 царств": "1samuel", "2 царств": "2samuel", "3 царств": "1kings", "4 царств": "2kings",
    "1 паралипоменон": "1chronicles", "2 паралипоменон": "2chronicles", ездра: "ezra", ездры: "ezra", неемия: "nehemiah", неемии: "nehemiah",
    есфирь: "esther", есфири: "esther", иов: "job", иова: "job", псалтирь: "psalms", псалом: "psalms", притчи: "proverbs", притч: "proverbs",
    екклесиаст: "ecclesiastes", екклесиаста: "ecclesiastes", "песнь песней": "songofsolomon", исаия: "isaiah", исаии: "isaiah",
    иеремия: "jeremiah", иеремии: "jeremiah", плач: "lamentations", "плач иеремии": "lamentations", иезекииль: "ezekiel", иезекииля: "ezekiel", 
    "книга пророка иезекииля": "ezekiel", "книга пророка исаии": "isaiah", "книга пророка иеремии": "jeremiah", "книга пророка даниила": "daniel",
    "книга пророка осии": "hosea", "книга пророка иоиля": "joel", "книга пророка амоса": "amos", "книга пророка авдия": "obadiah", 
    "книга пророка ионы": "jonah", "книга пророка михея": "micah", "книга пророка наума": "nahum", "книга пророка аввакума": "habakkuk",
    "книга пророка софонии": "zephaniah", "книга пророка аггея": "haggai", "книга пророка захарии": "zechariah", "книга пророка малахии": "malachi",
    "послание петра": "1peter", "1 послание петра": "1peter", "2 послание петра": "2peter", "послание иоанна": "1john", 
    "1 послание иоанна": "1john", "2 послание иоанна": "2john", "3 послание иоанна": "3john",
    даниил: "daniel", даниила: "daniel",
    осия: "hosea", осии: "hosea", иоиль: "joel", иоиля: "joel", амос: "amos", амоса: "amos", авдий: "obadiah", авдия: "obadiah", иона: "jonah", ионы: "jonah",
    михей: "micah", михея: "micah", наум: "nahum", наума: "nahum", аввакум: "habakkuk", аввакума: "habakkuk", софония: "zephaniah", софонии: "zephaniah",
    аггей: "haggai", аггея: "haggai", захария: "zechariah", захарии: "zechariah", малахия: "malachi", малахии: "malachi",
    матфей: "matthew", матфея: "matthew", марк: "mark", марка: "mark",
    лука: "luke", луки: "luke", иоанн: "john", иоанна: "john", деяния: "acts", деяний: "acts",
    римлянам: "romans", "1 коринфянам": "1corinthians", "2 коринфянам": "2corinthians",
    галатам: "galatians", ефесянам: "ephesians", "еф": "ephesians", "еф.": "ephesians", филиппийцам: "philippians",
    колоссянам: "colossians", "1 фессалоникийцам": "1thessalonians", "2 фессалоникийцам": "2thessalonians",
    "1 тимофею": "1timothy", "2 тимофею": "2timothy", титу: "titus", филимону: "philemon",
    евреям: "hebrews", "евр": "hebrews", "евр.": "hebrews", иакова: "james", иакову: "james", "1 петра": "1peter", "2 петра": "2peter",
    "1 иоанна": "1john", "2 иоанна": "2john", "3 иоанна": "3john", иуды: "jude", откровение: "revelation", откровения: "revelation"
  },
  es: {
    génesis: "genesis", genesis: "genesis", éxodo: "exodus", exodo: "exodus",
    levítico: "leviticus", levitico: "leviticus", números: "numbers", numeros: "numbers",
    deuteronomio: "deuteronomy", josué: "joshua", josue: "joshua", jueces: "judges", rut: "ruth",
    "1 samuel": "1samuel", "2 samuel": "2samuel", "1 reyes": "1kings", "2 reyes": "2kings",
    "1 crónicas": "1chronicles", "1 cronicas": "1chronicles", "2 crónicas": "2chronicles", "2 cronicas": "2chronicles",
    esdras: "ezra", nehemías: "nehemiah", nehemias: "nehemiah", ester: "esther", job: "job",
    salmos: "psalms", salmo: "psalms", proverbios: "proverbs", eclesiastés: "ecclesiastes", ecclesiastes: "ecclesiastes",
    "cantar de los cantares": "songofsolomon", isaías: "isaiah", isaias: "isaiah",
    jeremías: "jeremiah", jeremias: "jeremiah", lamentaciones: "lamentations", ezequiel: "ezekiel", daniel: "daniel",
    oseas: "hosea", joel: "joel", amós: "amos", amos: "amos", abdías: "obadiah", abdias: "obadiah",
    jonás: "jonah", jonas: "jonah", miqueas: "micah", nahúm: "nahum", nahum: "nahum",
    habacuc: "habakkuk", sofonías: "zephaniah", sofonias: "zephaniah", hageo: "haggai",
    zacarías: "zechariah", zacarias: "zechariah", malaquías: "malachi", malaquias: "malachi",
    mateo: "matthew", marcos: "mark", lucas: "luke", juan: "john", hechos: "acts",
    romanos: "romans", "1 corintios": "1corinthians", "2 corintios": "2corinthians",
    gálatas: "galatians", galatas: "galatians", efesios: "ephesians", filipenses: "philippians",
    colosenses: "colossians", "1 tesalonicenses": "1thessalonians", "2 tesalonicenses": "2thessalonians",
    "1 timoteo": "1timothy", "2 timoteo": "2timothy", tito: "titus", filemón: "philemon", filemon: "philemon",
    hebreos: "hebrews", santiago: "james", "1 pedro": "1peter", "2 pedro": "2peter",
    "1 juan": "1john", "2 juan": "2john", "3 juan": "3john", judas: "jude", apocalipsis: "revelation"
  }
};

const BibleVerse = ({ reference, compact = false }: BibleVerseProps) => {
  const { language } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const [verseData, setVerseData] = useState<BibleData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verseNotAvailable, setVerseNotAvailable] = useState(false);
  
  // Check if this is a chapter-only reference (book name + chapter number, no verse)
  const isChapterOnly = !reference.includes(':');

  useEffect(() => {
    if (verseData) {
      setVerseData(null);
      setIsExpanded(false);
    }
    // Reset availability when language changes
    setVerseNotAvailable(false);
  }, [language]);

  useEffect(() => {
    // Reset state when reference changes
    setVerseData(null);
    setIsExpanded(false);
    setError(null);
    setVerseNotAvailable(false);
  }, [reference]);

  const parseReference = (ref: string) => {
    // First try to match chapter-only reference (e.g., "Psalm 116" or "Псалом 116")
    const chapterOnlyMatch = ref.match(/^(.+?)\s+(\d+)$/i);
    if (chapterOnlyMatch) {
      // This is a chapter-only reference
      const bookInput = chapterOnlyMatch[1].trim().toLowerCase();
      const chapter = chapterOnlyMatch[2];
      
      console.log('📖 BibleVerse: Parsing chapter-only reference:', { 
        original: ref, 
        bookInput, 
        chapter,
        currentLanguage: language 
      });

      // Find the book name
      let bookKey = null;
      let foundInLanguage = null;
      
      const currentLangMap = BOOK_NAMES[language] || BOOK_NAMES.en;
      bookKey = currentLangMap[bookInput];
      if (bookKey) {
        foundInLanguage = language;
      }
      
      if (!bookKey) {
        for (const [lang, bookMap] of Object.entries(BOOK_NAMES)) {
          if (lang === language) continue;
          const key = bookMap[bookInput];
          if (key) {
            bookKey = key;
            foundInLanguage = lang;
            break;
          }
        }
      }

      if (!bookKey) {
        console.error('❌ BibleVerse: Book not found for chapter-only reference:', bookInput);
        return null;
      }

      const bookId = BOOK_IDS[bookKey];
      if (!bookId) {
        console.error('❌ BibleVerse: Book ID not found for:', bookKey);
        return null;
      }

      console.log('✅ BibleVerse: Successfully parsed chapter-only reference:', { 
        bookInput, 
        bookKey, 
        bookId,
        foundInLanguage,
        chapter
      });

      return { 
        bookId, 
        bookKey, 
        chapter, 
        verseStart: null, 
        verseEnd: null,
        isChapterOnly: true 
      };
    }

    // Handle both regular hyphens (-) and en-dashes (–) in verse ranges
    const match = ref.match(/^(.+?)\s+(\d+):(\d+)(?:[-–](\d+))?$/i);
    if (!match) {
      console.error('❌ BibleVerse: Regex did not match reference:', ref, 'Language:', language);
      return null;
    }

    let bookInput = match[1].trim().toLowerCase();
    const chapter = match[2];
    const verseStart = match[3];
    const verseEnd = match[4] || verseStart;

    console.log('📖 BibleVerse: Parsing reference:', { 
      original: ref, 
      bookInput, 
      chapter, 
      verseStart, 
      verseEnd, 
      currentLanguage: language 
    });

    // Try to find the book name in ANY language dictionary
    // This allows references stored in English to work when UI is in Russian, and vice versa
    let bookKey = null;
    let foundInLanguage = null;
    
    // First try the current language
    const currentLangMap = BOOK_NAMES[language] || BOOK_NAMES.en;
    bookKey = currentLangMap[bookInput];
    if (bookKey) {
      foundInLanguage = language;
    }
    
    // If not found, try all other languages
    if (!bookKey) {
      for (const [lang, bookMap] of Object.entries(BOOK_NAMES)) {
        if (lang === language) continue; // Already tried
        const key = bookMap[bookInput];
        if (key) {
          bookKey = key;
          foundInLanguage = lang;
          break;
        }
      }
    }

    if (!bookKey) {
      console.error('❌ BibleVerse: Book not found in any language:', bookInput);
      return null;
    }

    const bookId = BOOK_IDS[bookKey];
    if (!bookId) {
      console.error('❌ BibleVerse: Book ID not found for:', bookKey);
      return null;
    }

    console.log('✅ BibleVerse: Successfully parsed:', { 
      bookInput, 
      bookKey, 
      bookId,
      foundInLanguage,
      usingVersion: BIBLE_VERSIONS[language] || BIBLE_VERSIONS.en 
    });
    return { 
      bookId, 
      bookKey, 
      chapter, 
      verseStart: parseInt(verseStart), 
      verseEnd: parseInt(verseEnd),
      isChapterOnly: false 
    };
  };

  const fetchVerse = async () => {
    if (verseData || loading) {
      setIsExpanded(!isExpanded);
      return;
    }

    setLoading(true);
    setError(null);
    setIsExpanded(true);

    try {
      console.log('🔍 Starting fetchVerse for:', reference, 'Language:', language);
      const parsed = parseReference(reference);
      if (!parsed) {
        console.error('❌ Failed to parse reference:', reference);
        throw new Error(`Invalid format: "${reference}". Try: "John 3:16", "Иоанна 3:16", or "Juan 3:16"`);
      }

      const { bookId, bookKey, chapter, verseStart, verseEnd, isChapterOnly } = parsed;
      
      // If this is a chapter-only reference, don't fetch - just close the expansion
      if (isChapterOnly) {
        setIsExpanded(false);
        setLoading(false);
        return;
      }
      let version = BIBLE_VERSIONS[language] || BIBLE_VERSIONS.en;
      const versionsToTry = language === 'es' ? SPANISH_FALLBACKS : [version];

      console.log('✅ Fetching Bible verse:', { reference, bookId, bookKey, chapter, verseStart, verseEnd, primaryVersion: version, language, fallbacks: versionsToTry });

      // For verse ranges, use the parallel verses endpoint
      if (verseStart !== verseEnd) {
        const url = 'https://bolls.life/get-parallel-verses/';
        const verses = [];
        for (let v = verseStart; v <= verseEnd; v++) {
          verses.push(v);
        }

        let successfulTranslation = null;
        let finalText = '';

        // Try each translation in order
        for (const tryVersion of versionsToTry) {
          console.log('📡 Fetching verse range from:', url, 'Translation:', tryVersion);
          
          const requestBody = {
            translations: [tryVersion],
            verses: verses,
            book: bookId,
            chapter: parseInt(chapter)
          };
          console.log('📤 Request body:', requestBody);

          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody)
          });

          console.log('📥 Response status:', response.status, response.ok);

          if (!response.ok) {
            console.error('❌ API returned error status:', response.status);
            continue; // Try next translation
          }

          const data = await response.json();
          console.log('📖 Bible API response:', data);

          if (!Array.isArray(data) || data.length === 0 || !Array.isArray(data[0])) {
            console.warn('⚠️ Invalid response format, trying next translation');
            continue;
          }

          const verseTexts = data[0];
          console.log('Verse texts received:', verseTexts);
          
          if (verseTexts.length === 0) {
            console.warn('⚠️ No verses returned, trying next translation');
            continue;
          }

          const text = verseTexts.map((v: any) => {
            if (!v.text) {
              console.warn('⚠️ Verse missing text field:', v);
              return '';
            }
            const parser = new DOMParser();
            const doc = parser.parseFromString(v.text, 'text/html');
            doc.querySelectorAll('sup').forEach(sup => sup.remove());
            let cleanText = doc.body.textContent || doc.body.innerText || '';
            cleanText = cleanText.replace(/([a-zA-Z]+)\d+/g, '$1');
            return cleanText.trim();
          }).filter(t => t.length > 0).join(' ');

          console.log('Final cleaned text:', text);

          if (text.trim()) {
            finalText = text.trim();
            successfulTranslation = tryVersion;
            console.log('✅ Successfully fetched with translation:', tryVersion);
            break; // Found valid text, stop trying
          }
        }

        if (!finalText) {
          console.error('❌ All translations failed to return text');
          setVerseNotAvailable(true);
          setIsExpanded(false);
          return;
        }

        setVerseData({
          text: finalText,
          reference: `${reference.split(' ')[0]} ${chapter}:${verseStart}-${verseEnd}`,
          translation_name: version,
          actualTranslation: successfulTranslation || undefined
        });
      } else {
        // For single verse, try fallback translations
        let successfulTranslation = null;
        let finalText = '';
        let finalData = null;

        for (const tryVersion of versionsToTry) {
          const url = `https://bolls.life/get-verse/${tryVersion}/${bookId}/${chapter}/${verseStart}/`;
          console.log('📡 Bible API URL:', url);
          
          const response = await fetch(url);
          console.log('📥 Response status:', response.status, response.ok);
          
          if (!response.ok) {
            const errorText = await response.text();
            console.error('❌ API error:', response.status, errorText);
            
            if (response.status === 404) {
              console.warn('⚠️ Verse not found (404), trying next translation');
              continue; // Try next translation
            }
            continue; // Try next translation for other errors too
          }

          const data = await response.json();
          console.log('📖 Bible API response:', data);

          if (!data.text) {
            console.warn('⚠️ No text in response, trying next translation');
            continue;
          }

          // Remove HTML tags and Strong's numbers from text
          const parser = new DOMParser();
          const doc = parser.parseFromString(data.text, 'text/html');
          doc.querySelectorAll('sup').forEach(sup => sup.remove());
          let text = doc.body.textContent || doc.body.innerText || '';
          text = text.replace(/([a-zA-Z]+)\d+/g, '$1');

          console.log('Final cleaned text:', text);

          if (text.trim()) {
            finalText = text.trim();
            successfulTranslation = tryVersion;
            console.log('✅ Successfully fetched with translation:', tryVersion);
            break; // Found valid text, stop trying
          }
        }

        if (!finalText) {
          console.error('❌ All translations failed to return text');
          setVerseNotAvailable(true);
          setIsExpanded(false);
          return;
        }

        setVerseData({
          text: finalText,
          reference: `${reference.split(' ')[0]} ${chapter}:${verseStart}`,
          translation_name: version,
          actualTranslation: successfulTranslation || undefined
        });
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Could not load verse';
      setError(errorMessage);
      console.error('Bible API error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-2">
      {isChapterOnly ? (
        <Link 
          to="/bible" 
          state={{ chapterReference: reference }}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-primary text-primary-foreground rounded-md border border-border hover:bg-primary/80 transition-colors cursor-pointer"
        >
          <BookOpen className="h-4 w-4" />
          <span className="font-medium">{reference}</span>
        </Link>
      ) : verseNotAvailable ? (
        <div className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-muted text-muted-foreground rounded-md border border-border">
          <BookOpen className="h-4 w-4" />
          <span className="font-medium">{reference}</span>
          <span className="text-xs opacity-70">
            ({language === 'es' ? 'no disponible en esta traducción' : 
              language === 'ru' ? 'недоступно в этом переводе' : 
              'not available in this translation'})
          </span>
        </div>
      ) : (
        <>
          <Button
            variant="outline"
            size={compact ? "sm" : "default"}
            onClick={fetchVerse}
            className="w-full justify-between"
          >
            <span className="flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              {reference}
            </span>
            {isExpanded ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>

          {loading && (
            <Card className="bg-card border-border">
              <CardContent className="pt-6">
                <Skeleton className="h-20 w-full" />
              </CardContent>
            </Card>
          )}

          {error && (
            <Card className="border-destructive bg-card">
              <CardContent className="pt-6">
                <p className="text-sm text-destructive">{error}</p>
                <p className="text-xs text-muted-foreground mt-2">
                  Try a different format (e.g., "John 3:16" or "Psalm 23:1")
                </p>
              </CardContent>
            </Card>
          )}

          {verseData && isExpanded && (
            <Card className="bg-card border-border z-10">
              <CardContent className="pt-6 space-y-2">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {verseData.text}
                </p>
                <p className="text-xs text-muted-foreground text-right">
                  — {verseData.reference} ({verseData.actualTranslation || verseData.translation_name})
                </p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default BibleVerse;
